/*
 * Order-link capture: carries a helpdesk conversation token onto the cart.
 *
 * Support agents send umi.store links stamped with `?umi_cw=<token>`. The inline
 * half of `snippets/order-link-capture.liquid` lifts that token out of the URL
 * before any other script can read it and stores it for 30 days; this half copies
 * the stored token onto the cart as the `__cw` attribute, so it reaches the order's
 * note attributes and the conversation can be matched to the order it produced.
 *
 * The double underscore is Shopify's privacy marker for cart attributes: a `__`
 * attribute cannot be read back from Liquid or the Ajax API, so no other script on
 * the storefront origin can lift the token out of `/cart.js`. It still reaches the
 * order — that is the one thing the prefix does not change.
 *
 * Attribution is never worth a lost sale: nothing here throws, blocks add-to-cart,
 * or shows the shopper anything. Every failure path silently gives up attribution.
 *
 * The token helpers are pure and exported for tests; the browser wiring below
 * calls the same code.
 */
(function (global) {
  const STORAGE_KEY = "umi_cw_token";
  const APPLIED_KEY = "umi_cw_applied";
  const CART_ATTRIBUTE = "__cw";

  // Tokens are a few hundred characters. The cap keeps an arbitrary string from a
  // hostile URL out of storage and off the cart; the alphabet is deliberately not
  // policed, because wrongly rejecting a real token loses attribution silently.
  const MAX_TOKEN_LENGTH = 1024;

  function isUsableToken(token) {
    return (
      typeof token === "string" &&
      token !== "" &&
      token.length <= MAX_TOKEN_LENGTH
    );
  }

  // Reads a stored `{ token, expiresAt }` record. Anything malformed, expired or
  // written by an older/other version of this snippet is treated as "no token".
  function parseStoredToken(raw, now) {
    if (typeof raw !== "string" || raw === "") return null;

    let record;
    try {
      record = JSON.parse(raw);
    } catch (_error) {
      return null;
    }

    if (!record || !isUsableToken(record.token)) return null;
    if (typeof record.expiresAt !== "number" || record.expiresAt <= now)
      return null;

    return record.token;
  }

  // Storage first (it survives navigation), then the handover the inline capture
  // leaves on `window` when storage is unavailable — private browsing or blocked
  // cookies still get the landing page view attributed.
  function readToken(now) {
    try {
      const stored = parseStoredToken(
        global.localStorage.getItem(STORAGE_KEY),
        now,
      );
      if (stored) return stored;
    } catch (_error) {
      /* storage unavailable — fall through to the in-page handover */
    }

    const handover = global.UMIOrderLink && global.UMIOrderLink.token;
    return isUsableToken(handover) ? handover : null;
  }

  // `routes` is localized (markets prefix the path), so prefer the theme's value.
  function cartUpdateUrl() {
    const routes = global.routes;
    return routes && routes.cart_update_url
      ? routes.cart_update_url
      : "/cart/update";
  }

  function applyToken(token) {
    if (!token || typeof fetch !== "function") return Promise.resolve(false);

    const attributes = {};
    attributes[CART_ATTRIBUTE] = token;

    return fetch(cartUpdateUrl(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "same-origin",
      // The shopper may click straight off the landing page; without this the
      // request is cancelled by the navigation and the token is never applied.
      keepalive: true,
      body: JSON.stringify({ attributes }),
    })
      .then((response) => response.ok)
      .catch(() => false);
  }

  // A shopper who arrived from a link browses on with the token still stored.
  // Remembering what this session already wrote keeps that from re-posting the
  // same value on every page view.
  function alreadyApplied(token) {
    try {
      return global.sessionStorage.getItem(APPLIED_KEY) === token;
    } catch (_error) {
      return false;
    }
  }

  function markApplied(token) {
    try {
      global.sessionStorage.setItem(APPLIED_KEY, token);
    } catch (_error) {
      /* storage unavailable — the attribute is simply re-posted next page view */
    }
  }

  function wire(now) {
    const token = readToken(now);
    if (!token) return false;

    if (!alreadyApplied(token)) {
      applyToken(token).then((applied) => {
        if (applied) markApplied(token);
      });
    }

    // The shopper can end up checking out with a cart this session never stamped:
    // the load-time request may have failed, the cart cookie may have been lost or
    // rotated, and switching market or logging in can hand them a different cart.
    // Re-stamping on every cart mutation is the cheap way to catch all of those.
    //
    // Cart payloads that already carry the token are left alone, so working the
    // quantity stepper doesn't put a second write next to the theme's own on every
    // step. Being private does not hide the attribute here: `GET /cart.js` omits it,
    // but the cart *mutation* endpoints echo the full attribute map back to their
    // caller, and `/cart/change.js` is what the theme publishes as `cartData`.
    // If that ever stops being true the check simply never matches and every
    // mutation re-stamps — more requests, never a lost token. Add-to-cart responses
    // describe the line item rather than the cart and carry no attributes at all, so
    // those always re-stamp.
    //
    // The callback deliberately returns nothing: `publish` gathers subscriber
    // return values into a `Promise.all` that add-to-cart awaits, so handing back
    // the cart request would put a network round-trip in that path.
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS === "object") {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const current = readToken(Date.now());
        if (!current) return;

        const attributes = event && event.cartData && event.cartData.attributes;
        if (attributes && attributes[CART_ATTRIBUTE] === current) return;

        applyToken(current);
      });
    }

    return true;
  }

  if (typeof document !== "undefined") {
    // Apps on the storefront patch globals like `fetch`; a throw here would lose
    // the cart-update subscription and surface as a theme error in their logging.
    try {
      wire(Date.now());
    } catch (_error) {
      /* attribution gives up quietly, the page carries on */
    }
  }

  if (typeof module !== "undefined" && module.exports) {
    module.exports = {
      STORAGE_KEY,
      APPLIED_KEY,
      CART_ATTRIBUTE,
      MAX_TOKEN_LENGTH,
      parseStoredToken,
      readToken,
      cartUpdateUrl,
      applyToken,
      wire,
    };
  }
})(typeof window !== "undefined" ? window : globalThis);
